import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { magicLinkApi } from '../../services/api';

/**
 * Email-me-a-sign-in-link form.
 *
 * Backend returns a generic message regardless of whether the email exists -
 * the UI mirrors that copy ("If we have an account for that address...") so
 * we don't leak enumeration via behaviour differences.
 */
const MagicLinkForm = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await magicLinkApi.request(email);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-start gap-3">
        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-emerald-300 text-sm font-medium">{t('auth.magic.emailSentTitle')}</p>
          <p className="text-emerald-300/80 text-sm mt-1">{t('auth.magic.emailSentBody')}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}
      <div>
        <Label htmlFor="magic-email" className="text-gray-300">
          {t('auth.email')}
        </Label>
        <div className="relative mt-2">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input
            id="magic-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.email_placeholder')}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white"
          />
        </div>
      </div>
      <Button
        type="submit"
        disabled={loading || !email}
        className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold"
      >
        {loading ? t('common.loading') : t('auth.magic.requestLink')}
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
      <p className="text-xs text-gray-500 text-center">
        {t('auth.magic.expiryHint')}
      </p>
    </form>
  );
};

export default MagicLinkForm;
